import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { Watchable } from '@cotera/client/v0/actions/framework/watchable';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { ResourceType } from '@cotera/api';
import { resourceViewOf } from '@cotera/sdk/core';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  Folder,
  ChevronRight,
  ArrowLeft,
  Lock,
  Users,
  Globe,
  Home,
} from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { useAppModel, type FolderContentsData } from '../shell/app.model';

/** Folder scope from API: user (private), org (shared with org), external (other shared). */
type ResourceScope = 'user' | 'org' | 'external';

export type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

export type TargetResource = { type: ResourceType; id: string };

export type FolderPickerLabels = {
  pageTitle: string;
  pageSubtitleRoot: string;
  nestedBreadcrumbSubtitle: (folderName: string) => string;
  emptyNestedHint: string;
  nestedPrimaryButtonLabel: (folderName: string) => string;
};

const MOVE_DEFAULT_LABELS: FolderPickerLabels = {
  pageTitle: 'Move to folder',
  pageSubtitleRoot: 'Choose a folder to move this item into',
  nestedBreadcrumbSubtitle: (folderName: string) => `Inside "${folderName}"`,
  emptyNestedHint:
    'No subfolders here. You can move the item into this folder using the button above.',
  nestedPrimaryButtonLabel: (folderName: string) => `Move to "${folderName}"`,
};

export const SELECT_FOLDER_DEFAULT_LABELS: FolderPickerLabels = {
  pageTitle: 'Select folder',
  pageSubtitleRoot: 'Choose a folder',
  nestedBreadcrumbSubtitle: (folderName: string) => `Inside "${folderName}"`,
  emptyNestedHint:
    'No subfolders here. You can pick this folder using the button above.',
  nestedPrimaryButtonLabel: (folderName: string) => `Select "${folderName}"`,
};

type BreadcrumbEntry = {
  id: string;
  name: string;
  scope: ResourceScope;
  childrenCount: number | null;
};

const FOLDER_LIST_LIMIT = 100;
const EMPTY_FOLDER_CONTENTS_WATCHABLE =
  Watchable.fromValue<FolderContentsData>(null);
const EMPTY_FOLDER_CONTENTS_RESULT_WATCHABLE =
  Watchable.fromValue<FolderContentsData>({
    items: [],
    total: 0,
  });

async function fetchFolderPath(
  client: Pick<TenantedClient, 'resource'>,
  folderId: string
): Promise<BreadcrumbEntry[]> {
  const path: BreadcrumbEntry[] = [];
  let id: string | null = folderId;
  while (id !== null) {
    const res = await client.resource.get({ type: ResourceType.Folder, id });
    if (res.isErr()) {
      break;
    }
    const folder = res.value.type === 'folder' ? res.value.folder : null;
    if (folder === null) {
      break;
    }
    path.unshift({
      id: folder.id,
      name: folder.name,
      scope: folder.scope,
      childrenCount: folder.childrenCount ?? null,
    });
    id = folder.parentId;
  }
  return path;
}

function scopeLabel(scope: ResourceScope): string {
  switch (scope) {
    case 'user':
      return 'Private';
    case 'org':
      return 'Organization';
    case 'external':
      return 'External';
  }
}

const ConfirmScopeChangeView: React.FC<{
  fromScope: ResourceScope;
  toScope: ResourceScope;
  folderName: string;
  onConfirm: () => Promise<void>;
  onBack: () => void;
}> = ({ fromScope, toScope, folderName, onConfirm, onBack }) => {
  const [confirming, setConfirming] = useState(false);
  const fromLabel = scopeLabel(fromScope);
  const toLabel = scopeLabel(toScope);

  const handleConfirm = useCallback(async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  }, [onConfirm]);

  return (
    <ModalPageView
      title="Change folder visibility"
      subtitle={`Moving from ${fromLabel} to ${toLabel}`}
      className="px-4 py-4"
    >
      <div className="flex flex-col gap-4">
        <div
          className={cn(
            'flex flex-col gap-3 rounded-lg border p-4',
            'border-primary/30 bg-primary/5'
          )}
        >
          <p className="text-sm text-foreground">
            You are moving this item into{' '}
            <strong>&quot;{folderName}&quot;</strong>, which is in{' '}
            <strong>{toLabel}</strong> folders. It is currently in a{' '}
            <strong>{fromLabel}</strong> folder.
          </p>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              {fromScope === 'user' ? (
                <Lock className="h-4 w-4" />
              ) : fromScope === 'org' ? (
                <Users className="h-4 w-4" />
              ) : (
                <Globe className="h-4 w-4" />
              )}
              {fromLabel} → {toLabel}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {toScope === 'user'
              ? 'Private folders are only visible to you.'
              : toScope === 'org'
              ? 'Organization folders are visible to others in your org with access.'
              : 'External shared folders are visible to specific people with access.'}
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={confirming}
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={confirming}
          >
            {confirming ? <Loading.Spinner variant="sm" /> : 'Confirm move'}
          </Button>
        </div>
      </div>
    </ModalPageView>
  );
};

export type FolderPickerAskViewProps = {
  modalManager?: ModalManager | null;
  targetResource?: TargetResource | null;
  onRespond: (params: { folderId: string }) => void | Promise<void>;
  onCancel: () => void;
  labels?: Partial<FolderPickerLabels>;
  /**
   * When set, tapping a folder only highlights it—caller submits later (e.g. hijacker
   * wizard "Select"). When unset, `onRespond` runs immediately (move-to-folder).
   */
  onPickFolder?: (folderId: string) => void;
  /** Row id matching `highlightFolderId` gets pending-selection styling. */
  highlightFolderId?: string | null;
  /** When false, hides the in-card Cancel button (wizard skip handles dismiss). Default true. */
  showCancelButton?: boolean;
  /**
   * When provided, renders a "Move to top level" option that removes the target
   * from its current folder. Only shown when the target is known to be inside a
   * folder (via metadata) or when `alwaysShowMoveToTopLevel` is set.
   */
  onMoveToTopLevel?: () => void | Promise<void>;
  /** Force-show the "Move to top level" option even when the current folder can't be inferred. */
  alwaysShowMoveToTopLevel?: boolean;
};

/**
 * Shared folder browser UI used by Move to folder and Coco select-folder interrupt.
 */
export const FolderPickerAskView: React.FC<FolderPickerAskViewProps> = ({
  modalManager = null,
  targetResource = null,
  onRespond,
  onCancel,
  labels: labelsProp,
  onPickFolder,
  highlightFolderId = null,
  showCancelButton = true,
  onMoveToTopLevel,
  alwaysShowMoveToTopLevel = false,
}) => {
  const labels = useMemo(
    () => ({ ...MOVE_DEFAULT_LABELS, ...labelsProp }),
    [labelsProp]
  );

  const client = useTenantedClient();
  const org = useWhoami((s) => s.org);
  const appModel = useAppModel();
  const [loading, setLoading] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbEntry[]>([]);
  const [initialBreadcrumbSet, setInitialBreadcrumbSet] = useState(false);

  const { data: resourceData } = useQuery({
    queryKey:
      targetResource !== null
        ? queryKeys.resource.get({
            orgId: org.id,
            type: targetResource.type,
            id: targetResource.id,
          })
        : ['resource', 'none'],
    queryFn: async () => {
      if (targetResource === null) {
        return null;
      }
      return client.resource.get({
        type: targetResource.type,
        id: targetResource.id,
      });
    },
    enabled: targetResource !== null,
  });

  // Every resource type reports its own placement, so this no longer has to
  // enumerate which ones do — it used to name only chats and agents, which is
  // why the picker could not preselect a document's current folder.
  const currentFolderIdFromMeta =
    resourceData !== undefined && resourceData !== null && !resourceData.isErr()
      ? resourceViewOf(resourceData.value).folderId()
      : null;

  const { data: folderPathData } = useQuery({
    queryKey:
      currentFolderIdFromMeta !== null
        ? [org.id, 'folderPath', currentFolderIdFromMeta]
        : ['folderPath', 'none'],
    queryFn: () =>
      currentFolderIdFromMeta !== null
        ? fetchFolderPath(client, currentFolderIdFromMeta)
        : Promise.resolve([]),
    enabled: currentFolderIdFromMeta !== null,
  });

  React.useEffect(() => {
    if (
      initialBreadcrumbSet === false &&
      folderPathData !== undefined &&
      folderPathData.length > 0 &&
      breadcrumb.length === 0
    ) {
      // Use parent path so the current folder appears in the list and can be highlighted
      setBreadcrumb(folderPathData.slice(0, -1));
      setInitialBreadcrumbSet(true);
    }
  }, [initialBreadcrumbSet, folderPathData, breadcrumb.length]);

  const { data: descendantFolderIds } = useQuery({
    queryKey:
      targetResource?.type === ResourceType.Folder
        ? queryKeys.resource.folderDescendantIds({
            orgId: org.id,
            folderId: targetResource.id,
          })
        : ['resource', 'folder', 'descendantIds', 'none'],
    queryFn: async () => {
      if (targetResource?.type !== ResourceType.Folder) {
        return [];
      }
      const res = await client.resource.folder.getDescendantFolderIds({
        folderId: targetResource.id,
      });
      if (res.isErr()) {
        return [];
      }
      return res.value.folderIds;
    },
    enabled: targetResource?.type === ResourceType.Folder,
  });

  const forbiddenFolderIds = useMemo(() => {
    if (targetResource?.type !== ResourceType.Folder) {
      return new Set<string>();
    }
    return new Set([targetResource.id, ...(descendantFolderIds ?? [])]);
  }, [targetResource?.type, targetResource?.id, descendantFolderIds]);

  const privateFolderListWatchable = useMemo(() => {
    return appModel.getFolderListWatchable('private', FOLDER_LIST_LIMIT);
  }, [appModel]);
  const sharedFolderListWatchable = useMemo(() => {
    return appModel.getFolderListWatchable('shared', FOLDER_LIST_LIMIT);
  }, [appModel]);
  const privateFolderListData = useWatchableValue(privateFolderListWatchable);
  const sharedFolderListData = useWatchableValue(sharedFolderListWatchable);

  const currentFolder: BreadcrumbEntry | null =
    breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1] ?? null : null;
  const currentFolderContentsWatchable = useMemo(() => {
    if (currentFolder === null) {
      return EMPTY_FOLDER_CONTENTS_WATCHABLE;
    }
    if (currentFolder.childrenCount === 0) {
      return EMPTY_FOLDER_CONTENTS_RESULT_WATCHABLE;
    }
    return appModel.getFolderContentsWatchable({
      folderId: currentFolder.id,
      itemTypes: [ResourceType.Folder],
      limit: FOLDER_LIST_LIMIT,
    });
  }, [appModel, currentFolder]);
  const childrenData = useWatchableValue(currentFolderContentsWatchable);

  useEffect(() => {
    if (
      currentFolder !== null &&
      childrenData === null &&
      'refresh' in currentFolderContentsWatchable
    ) {
      void currentFolderContentsWatchable.refresh();
    }
  }, [childrenData, currentFolder, currentFolderContentsWatchable]);

  const privateFolders = useMemo(() => {
    const items = privateFolderListData?.items ?? [];

    const foldersWithTimestamps = items
      .filter((f) => !forbiddenFolderIds.has(f.id))
      .map((f) => ({
        ...f,
        updatedAtMs: new Date(f.updatedAt).getTime(),
      }));

    foldersWithTimestamps.sort((a, b) => b.updatedAtMs - a.updatedAtMs);

    return foldersWithTimestamps.map((f) => ({
      id: f.id,
      name: f.name,
      scope: f.scope,
      childrenCount: f.childrenCount,
    }));
  }, [privateFolderListData, forbiddenFolderIds]);

  const sharedFolders = useMemo(() => {
    return [...(sharedFolderListData?.items ?? [])]
      .filter((f) => !forbiddenFolderIds.has(f.id))
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
      .map((f) => ({
        id: f.id,
        name: f.name,
        scope: f.scope,
        childrenCount: f.childrenCount,
      }));
  }, [sharedFolderListData, forbiddenFolderIds]);

  const childFolders = useMemo(() => {
    if (childrenData === null || currentFolder === null) {
      return [];
    }
    type FolderItem = (typeof childrenData.items)[number];
    return childrenData.items
      .filter(
        (item): item is Extract<FolderItem, { type: 'folder' }> =>
          item.type === 'folder'
      )
      .map((item) => ({
        id: item.data.id,
        name: item.data.name,
        scope: item.data.scope,
        childrenCount: item.data.childrenCount ?? null,
      }))
      .filter((f) => !forbiddenFolderIds.has(f.id));
  }, [childrenData, currentFolder, forbiddenFolderIds]);
  const privateLoading = privateFolderListData === null;
  const sharedLoading = sharedFolderListData === null;
  const childrenLoading = currentFolder !== null && childrenData === null;

  const currentSourceScope = useMemo((): ResourceScope | null => {
    if (resourceData === undefined || resourceData === null) {
      return null;
    }
    if (resourceData.isErr()) {
      return null;
    }
    if (resourceData.value.type === 'folder') {
      return resourceData.value.folder.scope;
    }
    return resourceData.value.meta?.folderScope ?? null;
  }, [resourceData]);

  const commitOrPickFolder = useCallback(
    async (folderId: string) => {
      if (onPickFolder !== undefined) {
        onPickFolder(folderId);
        return;
      }
      setLoading(true);
      await onRespond({ folderId });
      setLoading(false);
    },
    [onPickFolder, onRespond]
  );

  const handleSelect = useCallback(
    async (
      folderId: string,
      targetScope: ResourceScope,
      folderName: string
    ) => {
      const scopeChange =
        currentSourceScope !== null &&
        targetScope !== null &&
        currentSourceScope !== targetScope;

      if (scopeChange) {
        if (modalManager === null || modalManager === undefined) {
          await commitOrPickFolder(folderId);
          return;
        }
        modalManager.push({
          view: () => (
            <ConfirmScopeChangeView
              fromScope={currentSourceScope}
              toScope={targetScope}
              folderName={folderName}
              onConfirm={async () => {
                modalManager.pop();
                await commitOrPickFolder(folderId);
              }}
              onBack={() => modalManager.pop()}
            />
          ),
          onClose: () => {},
        });
        return;
      }

      await commitOrPickFolder(folderId);
    },
    [currentSourceScope, modalManager, commitOrPickFolder]
  );

  const handleDrill = useCallback(
    (folder: {
      id: string;
      name: string;
      scope: ResourceScope;
      childrenCount: number | null;
    }) => {
      setBreadcrumb((prev) => [...prev, folder]);
    },
    []
  );

  const handleBack = useCallback(() => {
    setBreadcrumb((prev) => prev.slice(0, -1));
  }, []);

  const handleMoveToTopLevel = useCallback(async () => {
    if (onMoveToTopLevel === undefined) {
      return;
    }
    setLoading(true);
    await onMoveToTopLevel();
    setLoading(false);
  }, [onMoveToTopLevel]);

  const isRoot = breadcrumb.length === 0;
  const showTopLevelOption =
    isRoot &&
    onMoveToTopLevel !== undefined &&
    (currentFolderIdFromMeta !== null || alwaysShowMoveToTopLevel === true);
  const isLoadingRoot = privateLoading || sharedLoading;
  const hasAnyRootFolders =
    privateFolders.length > 0 || sharedFolders.length > 0;

  const subtitle =
    isRoot === true
      ? labels.pageSubtitleRoot
      : currentFolder !== null
      ? labels.nestedBreadcrumbSubtitle(currentFolder.name)
      : '';

  return (
    <ModalPageView
      title={labels.pageTitle}
      subtitle={subtitle}
      className="flex max-h-[min(72vh,36rem)] min-h-0 flex-col px-4 py-4"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {!isRoot && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            {currentFolder !== null ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() =>
                  void handleSelect(
                    currentFolder.id,
                    currentFolder.scope,
                    currentFolder.name
                  )
                }
                disabled={loading}
                className="shrink-0"
              >
                {labels.nestedPrimaryButtonLabel(currentFolder.name)}
              </Button>
            ) : null}
          </div>
        )}

        {showTopLevelOption && (
          <div className="shrink-0">
            <button
              type="button"
              onClick={() => void handleMoveToTopLevel()}
              disabled={loading}
              className={cn(
                'flex w-full items-center gap-3 rounded-md border border-transparent px-3 py-2.5 text-left transition-colors hover:border-border hover:bg-accent',
                loading && 'opacity-60 pointer-events-none'
              )}
            >
              <Home className="h-5 w-5 text-muted-foreground shrink-0" />
              <span className="font-medium text-foreground truncate">
                Move to top level
              </span>
              <span className="text-xs text-muted-foreground shrink-0">
                (no folder)
              </span>
            </button>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
          {isRoot ? (
            isLoadingRoot ? (
              <div className="flex items-center justify-center py-8">
                <Loading.Spinner variant="md" />
              </div>
            ) : !hasAnyRootFolders ? (
              <p className="text-sm text-muted-foreground py-4">
                No folders yet. Create a folder first from the sidebar.
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                {privateFolders.length > 0 && (
                  <section>
                    <h3 className="text-xs font-medium text-muted-foreground px-2 pb-1.5">
                      Private
                    </h3>
                    <ul className="flex flex-col gap-0.5">
                      {privateFolders.map((folder) => (
                        <FolderRow
                          key={folder.id}
                          folder={folder}
                          onSelect={() =>
                            void handleSelect(
                              folder.id,
                              folder.scope,
                              folder.name
                            )
                          }
                          onDrill={() => handleDrill(folder)}
                          disabled={loading}
                          isCurrentFolder={
                            currentFolderIdFromMeta === folder.id
                          }
                          isPendingChoice={highlightFolderId === folder.id}
                        />
                      ))}
                    </ul>
                  </section>
                )}
                {sharedFolders.length > 0 && (
                  <section>
                    <h3 className="text-xs font-medium text-muted-foreground px-2 pb-1.5">
                      Shared
                    </h3>
                    <ul className="flex flex-col gap-0.5">
                      {sharedFolders.map((folder) => (
                        <FolderRow
                          key={folder.id}
                          folder={folder}
                          onSelect={() =>
                            void handleSelect(
                              folder.id,
                              folder.scope,
                              folder.name
                            )
                          }
                          onDrill={() => handleDrill(folder)}
                          disabled={loading}
                          isCurrentFolder={
                            currentFolderIdFromMeta === folder.id
                          }
                          isPendingChoice={highlightFolderId === folder.id}
                        />
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            )
          ) : childrenLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loading.Spinner variant="md" />
            </div>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {childFolders.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2 px-2">
                  {labels.emptyNestedHint}
                </p>
              ) : (
                childFolders.map((folder) => (
                  <FolderRow
                    key={folder.id}
                    folder={folder}
                    onSelect={() =>
                      void handleSelect(folder.id, folder.scope, folder.name)
                    }
                    onDrill={() => handleDrill(folder)}
                    disabled={loading}
                    isCurrentFolder={currentFolderIdFromMeta === folder.id}
                    isPendingChoice={highlightFolderId === folder.id}
                  />
                ))
              )}
            </ul>
          )}
        </div>

        {showCancelButton === true ? (
          <div className="flex shrink-0 justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        ) : null}
      </div>
    </ModalPageView>
  );
};

function FolderRow({
  folder,
  onSelect,
  onDrill,
  disabled,
  isCurrentFolder,
  isPendingChoice,
}: {
  folder: {
    id: string;
    name: string;
    scope: ResourceScope;
    childrenCount: number | null;
  };
  onSelect: () => void;
  onDrill: () => void;
  disabled: boolean;
  isCurrentFolder?: boolean;
  isPendingChoice?: boolean;
}) {
  const hasSubfolders = (folder.childrenCount ?? 0) > 0;

  return (
    <li
      className={cn(
        'flex items-center gap-0 rounded-md overflow-hidden border transition-colors',
        isCurrentFolder === true
          ? 'border-primary/50 bg-primary/5'
          : isPendingChoice === true
          ? 'border-primary/60 bg-primary/10'
          : 'border-transparent hover:border-border'
      )}
    >
      <button
        type="button"
        className={cn(
          'flex items-center gap-3 flex-1 min-w-0 px-3 py-2.5 text-left hover:bg-accent transition-colors',
          disabled && 'opacity-60 pointer-events-none'
        )}
        onClick={onSelect}
        disabled={disabled}
      >
        <Folder className="h-5 w-5 text-muted-foreground shrink-0" />
        <span className="font-medium text-foreground truncate">
          {folder.name}
        </span>
        {isCurrentFolder === true && (
          <span className="text-xs text-muted-foreground shrink-0">
            (current)
          </span>
        )}
      </button>
      {hasSubfolders && (
        <button
          type="button"
          className={cn(
            'p-2 shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors',
            disabled && 'opacity-60 pointer-events-none'
          )}
          onClick={(e) => {
            e.stopPropagation();
            onDrill();
          }}
          disabled={disabled}
          aria-label="Open subfolders"
        >
          <ChevronRight className="h-4 w-5" />
        </button>
      )}
    </li>
  );
}
